import { useState, useEffect, useCallback } from "react";
import { useUserId } from "./useUserId";

interface LinkHubStats {
  shortCode: string;
  totalPageViews: number;
  totalItemClicks: number;
  itemClickCounts: Record<string, number>;
  firstScan: Date | null;
  lastScan: Date | null;
  recentScans: Array<{ timestamp: Date; itemId?: string }>;
}

export const useLinkHubStats = (shortCode: string | null | undefined) => {
  const [stats, setStats] = useState<LinkHubStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userId = useUserId();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchStats = useCallback(async () => {
    if (!shortCode || !userId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${backendUrl}/linktree/stats/${shortCode}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "client-principal-user-id": userId,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 403) {
        setError("Unauthorized");
      } else {
        setError("Failed to fetch stats");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [shortCode, userId, backendUrl]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
